"use client";
import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

// Define pricing tiers
const pricingTiers = {
  1: 15,
  3: 35,
  5: 50,
  7: 60,
};

const allowedQuantities = Object.keys(pricingTiers)
  .map(Number)
  .sort((a, b) => a - b);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [promoCode, setPromoCode] = useState(null); // For future promo code support
  const navigate = useNavigate();

  // ✅ Load cart from localStorage on mount (only on client side)
  useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = JSON.parse(localStorage.getItem("cart")) || [];
      setCart(stored);
    }
  }, []);

  // ✅ Persist cart to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("cart", JSON.stringify(cart));
    }
  }, [cart]);

  // ✅ Listen for cart cleared event (same tab)
  useEffect(() => {
    const handleCartCleared = () => {
      setCart([]);
    };

    window.addEventListener('cartCleared', handleCartCleared);

    return () => window.removeEventListener('cartCleared', handleCartCleared);
  }, []);

  // ✅ Add item to cart
  const addToCart = (product, quantity = 1) => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/login");
      return;
    }

    // Remove subscription cart if any
    const subCart = localStorage.getItem("sub_cart");
    if (subCart) {
      localStorage.removeItem("sub_cart");
      setCart((prev) => prev.filter((item) => item.type !== "subscription"));
    }

    const priceForQuantity = pricingTiers[quantity];

    setCart((prev) => {
      const exists = prev.find((item) => item.id === product.id);
      if (exists) {
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, quantity, price: priceForQuantity }
            : item
        );
      } else {
        return [
          ...prev,
          { ...product, quantity, price: priceForQuantity, type: "product" },
        ];
      }
    });
  };

  // ✅ Remove item from cart
  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  // ✅ Increment quantity to the next allowed tier
  const incrementQty = (id) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const currentIndex = allowedQuantities.indexOf(item.quantity);
          const nextIndex = Math.min(
            currentIndex + 1,
            allowedQuantities.length - 1
          );
          const nextQty = allowedQuantities[nextIndex];
          return { ...item, quantity: nextQty, price: pricingTiers[nextQty] };
        }
        return item;
      })
    );
  };

  // ✅ Decrement quantity to the previous allowed tier
  const decrementQty = (id) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const currentIndex = allowedQuantities.indexOf(item.quantity);
          const prevIndex = Math.max(currentIndex - 1, 0);
          const prevQty = allowedQuantities[prevIndex];
          return { ...item, quantity: prevQty, price: pricingTiers[prevQty] };
        }
        return item;
      })
    );
  };

  // ✅ Clear the entire cart
  const clearCart = () => setCart([]);

  // ✅ Calculate total
  const getTotal = () => {
    let total = cart.reduce((sum, item) => sum + item.price, 0);

    // Placeholder for applying promo codes later
    // if (promoCode) total = applyPromoDiscount(total, promoCode);

    return total;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        incrementQty,
        decrementQty,
        clearCart,
        getTotal,
        promoCode,
        setCart,
        setPromoCode,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
